// src/components/LegalModal.jsx
import React from 'react';
import styled, { keyframes } from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import { useApp } from '../contexts/AppContext';

const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const slideUp = keyframes`from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}`;

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 200;
  background: rgba(15, 23, 42, 0.55);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;
`;

const Sheet = styled.div`
  background: var(--surface); color: var(--text-1);
  border-radius: 20px; max-width: 640px; width: 100%;
  max-height: 82vh; display: flex; flex-direction: column;
  box-shadow: var(--shadow-l);
  animation: ${slideUp} 0.25s ease;
`;

const Head = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px; border-bottom: 1px solid var(--border);
`;

const Title = styled.h2`font-size: 17px; font-weight: 800;`;

const CloseBtn = styled.button`
  background: var(--surface-2); border: 1px solid var(--border);
  width: 32px; height: 32px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  color: var(--text-3); cursor: pointer;
  &:hover { color: var(--text-1); }
`;

const Body = styled.div`
  padding: 20px 24px 28px; overflow-y: auto; font-size: 13px; line-height: 1.65; color: var(--text-2);
`;

const H3 = styled.h3`font-size: 13.5px; font-weight: 700; color: var(--text-1); margin: 18px 0 6px; &:first-child{margin-top:0;}`;
const P  = styled.p`margin-bottom: 8px;`;
const Ul = styled.ul`margin: 0 0 8px 18px; padding: 0;`;
const Li = styled.li`margin-bottom: 4px;`;

const PRIVACY = (
  <>
    <H3>1. Какие данные собираются</H3>
    <Ul>
      <Li>Загруженный файл (DOCX/PDF) — обрабатывается для анализа и сохраняется в облачном хранилище.</Li>
      <Li>Имя файла, размер, тип, результаты проверки (баллы, найденные замечания), дата и время проверки.</Li>
      <Li>Стандартные технические данные хостинга (IP-адрес, User-Agent) в логах инфраструктуры Railway.</Li>
    </Ul>
    <H3>2. Для чего</H3>
    <P>Чтобы провести автоматическую проверку документа и сформировать сертификат, подлинность которого можно подтвердить по QR-коду.</P>
    <H3>3. Публичная проверка сертификата — важно</H3>
    <P>Каждому результату присваивается ссылка проверки вида myeducheck.uz/verify/&lt;id&gt;. Любой, у кого есть эта ссылка или QR-код с сертификата, увидит имя файла, дату проверки, баллы и оценку. Сам загруженный файл по этой ссылке не публикуется и не скачивается.</P>
    <H3>4. Где хранятся данные</H3>
    <P>MongoDB и S3-совместимое объектное хранилище на инфраструктуре Railway. Мы не продаём и не передаём данные третьим лицам, кроме технических провайдеров, необходимых для работы сервиса (хостинг, база данных, хранилище файлов).</P>
    <H3>5. Хранение и удаление</H3>
    <P>Данные хранятся, пока это нужно для работы функции проверки подлинности сертификата. Если хотите удалить свои данные — напишите на почту или в Telegram, указанные в футере сайта.</P>
    <H3>6. Важная оговорка</H3>
    <P>MyEduCheck — независимый автоматизированный инструмент, не является официальным органом нормоконтроля учебного заведения. Результаты проверки носят рекомендательный характер.</P>
  </>
);

const TERMS = (
  <>
    <H3>1. Характер сервиса</H3>
    <P>Сервис предоставляется «как есть», без гарантий абсолютной точности автоматической проверки. Результат MyEduCheck не заменяет официальный нормоконтроль вуза и должен использоваться как вспомогательный инструмент.</P>
    <H3>2. Ответственность пользователя</H3>
    <Ul>
      <Li>Вы подтверждаете, что имеете право загружать проверяемый файл (например, это ваша собственная учебная работа).</Li>
      <Li>Запрещено использовать сервис для загрузки вредоносных файлов, попыток несанкционированного доступа или создания чрезмерной нагрузки на инфраструктуру.</Li>
    </Ul>
    <H3>3. Ограничение ответственности</H3>
    <P>Мы не несём ответственности за решения, принятые на основании результатов проверки (например, оценку аттестационной комиссии), а также за любые убытки, связанные с использованием или невозможностью использования сервиса.</P>
    <H3>4. Изменения сервиса</H3>
    <P>Сервис в настоящий момент бесплатен. Функциональность, условия и доступность сервиса могут изменяться без предварительного уведомления.</P>
    <H3>5. Контакты</H3>
    <P>По всем вопросам — email, Telegram и сайт, указанные в футере главной страницы.</P>
  </>
);

export default function LegalModal({ type, onClose }) {
  const { t } = useApp();
  if (!type) return null;
  const isPrivacy = type === 'privacy';
  return (
    <Overlay onClick={onClose}>
      <Sheet onClick={(e) => e.stopPropagation()}>
        <Head>
          <Title>{isPrivacy ? t.legalPrivacyBtn : t.legalTermsBtn}</Title>
          <CloseBtn onClick={onClose} aria-label="Закрыть"><CloseIcon sx={{ fontSize: 18 }} /></CloseBtn>
        </Head>
        <Body>{isPrivacy ? PRIVACY : TERMS}</Body>
      </Sheet>
    </Overlay>
  );
}
